import {appConfig} from "./config";
import {lazy} from "react";

const getParameterByName = (name, url = window.location.href) => {
    name = name.replace(/[[\]]/g, "\\$&");
    var regex = new RegExp("[?&]" + name + "(=([^&#]*)|&|#|$)"),
        results = regex.exec(url);
    if (!results) return null;
    if (!results[2]) return "";
    return decodeURIComponent(results[2].replace(/\+/g, " "));
};

const ALLOWED_ROOT_DOMAINS = ["caresource.com", "mstruecare.com"];

const isAllowedOrigin = (origin) => {
    try {
        const {hostname, protocol} = new URL(origin);
        if (protocol !== "https:") return false;
        return ALLOWED_ROOT_DOMAINS.some(
            (domain) => hostname === domain || hostname.endsWith("." + domain)
        );
    } catch {
        return false;
    }
};

const postMessageData = (data, targetOrigin) => {
    let targetOrigins = ["*"];

    // Handle known URLs
    if (!appConfig.debugAllowed) {
        if (targetOrigin) {
            if (targetOrigin.includes("www.caresource.com")) {
                targetOrigins = ["https://www.caresource.com"];
            } else if (targetOrigin.includes("staging.caresource.com")) {
                targetOrigins = ["https://staging.caresource.com"];
            } else if (targetOrigin.includes("caresource.com")) {
                targetOrigins = ["https://caresource.com"];
            }
        } else {
            // Since we can't get the parent across subdomains we need to send the message to all allowed origins
            // This throws an error for the other domains, but ¯\_(ツ)_/¯
            targetOrigins = [
                "https://caresource.com",
                "https://www.caresource.com",
                "https://staging.caresource.com",
            ];
        }
    }

    if (window.opener || (window.parent !== window.self)) {
        var target = window.opener || window.parent;

        if (appConfig.debugAllowed) {
            console.log("PAYLOAD:", data);
        }

        for (const origin of targetOrigins) {
            target.postMessage(data, origin);
        }
    }
};

const postMessageToOrigin = (data, targetOrigin) => {
    if (!(window.opener || (window.parent !== window.self))) return false;
    const target = window.opener || window.parent;

    if (appConfig.debugAllowed) {
        console.log("PAYLOAD:", data);
        target.postMessage(data, "*");
        return true;
    }

    if (!targetOrigin) return false;
    let origin;
    try {
        origin = getBaseUrl(targetOrigin);
    } catch {
        return false;
    }
    if (!isAllowedOrigin(origin)) return false;
    target.postMessage(data, origin);
    return true;
};

const postLoginData = (payloadData, preloaded, targetOrigin) => {
    const payload = payloadData.encrypted;
    const obj = payloadData.message;

    const authData = {
        key: "caresource-connect-api",
        action: "user-authenticated",
        value: true,
    };

    const data = {
        type: "caresource-connect",
        payload: payload,
        obj: obj,
    };

    var newData = {
        key: "caresource-connect-api",
        action: preloaded ? "member-preloaded" : "member-selected",
        value: {
            payload: payload,
            data: obj,
        },
    };

    postMessageData(authData, targetOrigin);
    postMessageData(data, targetOrigin);
    postMessageData(newData, targetOrigin);
};


const validateEmailRegex = email => !/^[A-Z0-9._+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)

const getBaseUrl = (url) => {
    const parsedUrl = new URL(url);
    return `${parsedUrl.protocol}//${parsedUrl.host}`;
}

const getCookie = (cname) => {
    let name = cname + "=";
    let decodedCookie = decodeURIComponent(document.cookie);
    let ca = decodedCookie.split(";");
    for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === " ") {
            c = c.substring(1);
        }
        if (c.indexOf(name) === 0) {
            return c.substring(name.length, c.length);
        }
    }
    return null;
};

const getLanguageSettings = (value) => {
    const acceptedLanguages = appConfig.acceptedLanguages;
    let language = {
        locale: acceptedLanguages["en"][3],
        preferredLanguage: acceptedLanguages["en"][0],
    };

    if (!value) return language;
    const normalized = value.trim().toLowerCase();

    Object.keys(acceptedLanguages).forEach((key) => {
        if (
            acceptedLanguages[key]
                .map((lang) => lang.toLowerCase())
                .includes(normalized)
        ) {
            language.locale = acceptedLanguages[key][3];
            language.preferredLanguage = acceptedLanguages[key][0];
        }
    });

    return language;
}

export {getParameterByName, postMessageData, postMessageToOrigin, postLoginData, validateEmailRegex, getBaseUrl, getCookie, getLanguageSettings};

const routeComponents = {
  Home: () => import("./screens/Home"),
  Logout: () => import("./screens/Logout"),
  Impersonate: () => import("./screens/Impersonate"),
  Refresh: () => import("./screens/Refresh"),
  SSOStaff: () => import("./screens/SSOStaff"),
  SSOUserRedirect: () => import("./screens/SSOUserRedirect"),
  Account: () => import("./screens/Account"),
  SelectMember: () => import("./screens/SelectMember"),
  Guest: () => import("./screens/Guest"),
  Registration: () => import("./screens/registration/Registration"),
  RegistrationComplete: () => import("./screens/registration/RegistrationComplete"),
  SignIn: () => import("./screens/account/Signin"),
  MFAEnroll: () => import("./screens/mfa/MFAEnroll"),
  AccountVerification: () => import("./screens/account/verification/AccountVerification"),
  AccountSession: () => import("./screens/account/AccountSession"),
  MFASMSEnroll: () => import("./screens/mfa/MFASMSEnroll"),
  MFAEmailEnroll: () => import("./screens/mfa/MFAEmailEnroll"),
  MFAChallenge: () => import("./screens/mfa/MFAChallenge"),
  AccountUnlocked: () => import("./screens/account/unlocking/AccountUnlocked"),
  EmailSent: () => import("./screens/account/unlocking/EmailSent"),
  ValidateToken: () => import("./screens/account/ValidateToken"),
  PasswordReset: () => import("./screens/recovery/PasswordReset"),
  ForgotPassword: () => import("./screens/recovery/ForgotPassword"),
  PasswordResetComplete: () => import("./screens/recovery/PasswordResetComplete"),
  ValidatedRoute: () => import("./screens/account/components/ValidatedRoute"),
  SignOut: () => import("./screens/account/Signout"),
  ConnectAccount: () => import("./screens/account/verification/ConnectAccount"),
  ConnectAccountRelation: () => import("./screens/account/verification/ConnectAccountRelation"),
  MFAChallengeTotp: () => import("./screens/mfa/MFAChallengeTotp"),
  PasswordUpdate: () => import("./screens/recovery/PasswordUpdate"),
  MantineLayout: () => import("./components/MantineLayout"),
  WhyConnectPlan: () => import("./screens/account/verification/WhyConnectPlan"),
  Login: () => import("./screens/Login"),
  AuthenticationCallback: () => import("./screens/account/components/AuthenticationCallback"),
  RequestError: () => import("./screens/RequestError")
};

const createLazyRouteComponents = (components) =>
  Object.fromEntries(
    Object.entries(components).map(([name, importFn]) => [name, lazy(importFn)])
  );

export const lazyRouteComponents = createLazyRouteComponents(routeComponents);